import { Link } from "react-router-dom"
import { MapPin, Briefcase, ExternalLink, Clock, Wifi } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { formatRelativeTime } from "@/lib/utils"
import type { JobWithAuthor } from "@/types"

const jobTypeLabels: Record<string, string> = {
  "full-time": "Full-time",
  "part-time": "Part-time",
  contract: "Contract",
  freelance: "Freelance",
}

function formatSalary(min: number | null, max: number | null): string | null {
  if (!min && !max) return null
  const fmt = (n: number) => (n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`)
  if (min && max) return `${fmt(min)} – ${fmt(max)}`
  if (min) return `From ${fmt(min)}`
  return `Up to ${fmt(max!)}`
}

interface JobCardProps {
  job: JobWithAuthor
}

export function JobCard({ job }: JobCardProps) {
  const salary = formatSalary(job.salaryMin, job.salaryMax)

  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-md">
      <div className="flex flex-col p-4 sm:p-5">
        {/* Company & Meta */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              {job.companyName}
            </p>
            <Link to={`/jobs/${job.slug}`} className="group">
              <h3 className="mt-1 text-lg font-bold text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400 line-clamp-2">
                {job.title}
              </h3>
            </Link>
          </div>
          <Button asChild size="sm" variant="outline" className="shrink-0">
            <a href={job.applyUrl} target="_blank" rel="noopener noreferrer">
              Apply <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          </Button>
        </div>

        {/* Excerpt */}
        {job.excerpt && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
            {job.excerpt}
          </p>
        )}

        {/* Badges & Info */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            <Briefcase className="mr-1 h-3 w-3" />
            {jobTypeLabels[job.jobType] || job.jobType}
          </Badge>

          {job.location && (
            <Badge variant="secondary" className="text-xs">
              <MapPin className="mr-1 h-3 w-3" />
              {job.location}
            </Badge>
          )}

          {job.isRemote && (
            <Badge variant="default" className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-100">
              <Wifi className="mr-1 h-3 w-3" />
              Remote
            </Badge>
          )}

          {salary && (
            <Badge variant="secondary" className="text-xs">
              {salary}
            </Badge>
          )}

          <span className="ml-auto flex items-center text-xs text-gray-400 dark:text-gray-500">
            <Clock className="mr-1 h-3 w-3" />
            {formatRelativeTime(new Date(job.createdAt))}
          </span>
        </div>
      </div>
    </Card>
  )
}
